/**
 * Subscription Context
 * Provides subscription state and methods throughout the application
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import paymentService from '../services/paymentService';
import { useAuth } from '../hooks/useAuth';

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [devMode, setDevMode] = useState(false);

  // Fetch subscription and usage data
  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setSubscription(null);
      setUsage(null);
      setDevMode(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [subData, usageData] = await Promise.all([
        paymentService.getSubscription(),
        paymentService.getUsage(),
      ]);

      // Check for dev mode from subscription response
      if (subData && subData.dev_mode) {
        setDevMode(true);
      } else {
        setDevMode(false);
      }

      // Set subscription only if has_subscription is true or dev_mode is true
      if (subData && (subData.has_subscription || subData.dev_mode)) {
        setSubscription(subData);
      } else {
        setSubscription(null);
      }

      setUsage(usageData);
    } catch (err) {
      console.error('Error fetching subscription data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch plans (public, doesn't require auth)
  const fetchPlans = useCallback(async () => {
    try {
      const plansData = await paymentService.getPlans();
      setPlans(plansData);
    } catch (err) {
      console.error('Error fetching plans:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    refresh();
    fetchPlans();
  }, [refresh, fetchPlans]);

  // Computed properties
  // In dev mode, always consider user as premium
  const isPremium = devMode || (subscription && ['active', 'trialing'].includes(subscription.status));

  const currentPlan = plans.find(p => p.id === subscription?.plan_id);

  const canUploadVideo = () => {
    // Dev mode bypasses all subscription checks
    if (devMode) {
      return { allowed: true, reason: null, message: 'Dev mode - unlimited uploads' };
    }

    if (!isPremium) {
      return {
        allowed: false,
        reason: 'subscription_required',
        message: 'An active subscription is required to upload videos.',
      };
    }

    if (!usage) {
      return { allowed: true, reason: null, message: 'Upload available' };
    }

    if (usage.unlimited) {
      return { allowed: true, reason: null, message: 'Unlimited uploads' };
    }

    const remaining = usage.remaining || 0;
    if (remaining <= 0) {
      return {
        allowed: false,
        reason: 'limit_reached',
        message: `Upload limit reached (${usage.video_upload_limit} videos this billing period). Upgrade to Elite for unlimited uploads.`,
      };
    }

    return {
      allowed: true,
      reason: null,
      message: `${remaining} uploads remaining`,
      remaining,
    };
  };

  // Actions
  const startCheckout = async (planId) => {
    try {
      const checkoutUrl = await paymentService.createCheckoutSession(planId);
      window.location.href = checkoutUrl;
    } catch (err) {
      console.error('Error starting checkout:', err);
      throw err;
    }
  };

  const cancelSubscription = async () => {
    try {
      const result = await paymentService.cancelSubscription();
      await refresh(); // Refresh subscription data
      return result;
    } catch (err) {
      console.error('Error canceling subscription:', err);
      throw err;
    }
  };

  const value = {
    // State
    subscription,
    usage,
    plans,
    loading,
    error,
    devMode,

    // Computed
    isPremium,
    currentPlan,
    isElite: subscription?.plan_id === 'elite',
    isPro: subscription?.plan_id === 'pro',

    // Methods
    canUploadVideo,
    startCheckout,
    cancelSubscription,
    refresh,
    formatPrice: paymentService.formatPrice,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
